import * as queryString from 'query-string';
import * as React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import * as _ from 'underscore';

import { BrowserTitleService } from '~commonInfra/services/BrowserTitleService';
import { ISanitizer, sanitize } from '~commonInfra/utils/Sanitize';
import { CompositeSessionInfoSelectors, SessionSiteSelectors } from '~selectors/SessionInfoSelectors';
import { setOrderAction } from '~src/app/actions/SetOrderAction';
import { contentCountsUpToDate } from '~src/app/actions/spaces/ContentCountsActions';
import { ContentSpaceInfo,enterContentSpace } from '~src/app/actions/spaces/EnterSpaceAction';
import { addContentFavorite, removeContentFavorite } from '~src/app/actions/spaces/FavoritesActions';
import { openContentDetailDialog } from '~src/app/actions/spaces/OpenContentDetailDialogActions';
import { requestSpaceContents } from '~src/app/actions/spaces/RequestSpaceContentsActions';
import { setPreviousUrl } from '~src/app/actions/spaces/SetPreviousUrlReducerAction';
import {
  addContentTypeToProjectHierarchy,
  populateBreadcrumbsForPS,
  populateContentBreadcrumbs} from '~src/app/content/breadcrumbs/BreadcrumbHelpers';
import { AppState } from '~src/app/state/AppState';
import { SpaceInfo, WorkbookSubspaceName, workbookSubspaceNameToExplorableItemType } from '~src/app/state/SpaceInfo';
import { UrlParamKeys } from '~src/app/UrlParamKeys';
import { MapDispatchToProps, MapStateToProps } from '~src/app/utils/ConnectUtils';
import { ExtractActionStatusService } from '~src/extract/services/ExtractActionStatusService';
import { Projects } from '~src/projects/resources/Projects';
import { RequestAccessError } from '~src/requestAccess/components/RequestAccessError';
import { FeatureFlags } from '~src/ts/FeatureFlags';
import { Filter } from '~src/ts/helpers/Filter';
import { ContentTypeFilterKey } from '~src/ts/models/ContentFilters';
import { ExplorableItemType } from '~src/ts/models/ContentType';
import { IContentInfo } from '~src/ts/models/IContentInfo';
import { IFavoritableItemInfo, INamedItemInfo, ISpaceTypeInfo } from '~src/ts/models/resourceInfoParts';
import { getDefaultOrderForContentTypesOrSpaceInfo } from '~src/ts/models/sort/SortOptionDefinitions';
import {
  callViaApiService,
  GetLocationForContentRequestParams,
  GetLocationForContentResponse
} from '~src/ts/restApi/location/getLocationForContent';
import { IOrderClause } from '~src/ts/ServerApi/types/IOrderClause';
import { PublishWorkbookAction } from '~src/ts/services/PublishWorkbookAction';
import { IWorkbookDetailInfo, IWorkbookInfo } from '~src/workbook/models/IWorkbookInfo';
import { Workbooks, WorkbooksResource } from '~src/workbook/resources/Workbooks';
import { IWorkbookActions } from '~src/workbook/ServerApi/types/IWorkbookActions';

import { getContentTypesFromQueryParam, getOrderClausesFromQueryParam } from '../../app/spaces/Explorer/ExplorerUrlBindings';
import {
  ConnectedQueryParamUpdateProvider,
  QueryParamUpdateProviderOwnProps
} from '../../app/spaces/RouterHelpers/QueryParamUpdateProvider';
import { RouterError } from '../../app/spaces/RouterHelpers/RouterError';
import { SpacesSiteUrls } from '../../app/spaces/SpacesSiteUrls';
import { ConnectedWorkbookSpace } from './ConnectedWorkbookSpace';
import { WorkbookSpaceOwnProps } from './WorkbookSpace';


export interface WorkbookSpaceProviderOwnProps {
  readonly routeLocationSearch: string;
  readonly siteName: string;
  readonly subspaceName: WorkbookSubspaceName;
  readonly workbookId: string;
  readonly publishResultDisplay: boolean;
  readonly publishResultUrl: string;
  readonly urls: SpacesSiteUrls;
  readonly renderWorkbookSpace: (props: WorkbookSpaceOwnProps) => JSX.Element;
  readonly fetchedAllData: () => void;
}

export interface WorkbookSpaceProviderDispatchProps {
  readonly contentCountsUpToDate: () => void;
  readonly enterContentSpace: (contentSpaceInfo: ContentSpaceInfo) => void;
  readonly requestSpaceContents: () => void;
  readonly setOrderAction: (order: ReadonlyArray<IOrderClause>) => void;
  readonly addContentFavorite: (item: IFavoritableItemInfo) => void;
  readonly removeContentFavorite: (item: IFavoritableItemInfo) => void;
  readonly setPreviousUrl: (previousUrl: string) => void;
  readonly openContentDetailDialog: (content: IContentInfo, availableActions: IWorkbookActions, location: string) => void;
}

export interface WorkbookSpaceProviderPropsFromServices {
  readonly workbooks: WorkbooksResource;
  readonly sanitizer: ISanitizer;
  readonly extractActionStatusService: ExtractActionStatusService;
}

export interface WorkbookSpaceProviderStateProps {
  readonly isRequestAccessEnabled: boolean;
  readonly isWebExtractionEnabled: boolean;
}

export type WorkbookSpaceNonServicesProps =  WorkbookSpaceProviderStateProps & WorkbookSpaceProviderOwnProps & WorkbookSpaceProviderDispatchProps;

export type WorkbookSpaceProviderContainerProps =  WorkbookSpaceProviderStateProps & WorkbookSpaceProviderOwnProps &  WorkbookSpaceProviderDispatchProps & WorkbookSpaceProviderPropsFromServices;

export interface WorkbookSpaceProviderContainerState {
  readonly workbookActions: IWorkbookActions;
  readonly workbookInfo: IWorkbookInfo;
  readonly workbookDetailInfo: IWorkbookDetailInfo;
  readonly initialRender: boolean;
  readonly renderChildren: boolean;
  readonly hasError: boolean;
  readonly errorCode: number;
  readonly location: string;
}

const InitialState: WorkbookSpaceProviderContainerState = {
  workbookActions: undefined,
  workbookDetailInfo: undefined,
  workbookInfo: undefined,
  initialRender: true,
  renderChildren: false,
  hasError: false,
  errorCode: undefined,
  location: undefined
};

export class WorkbookSpaceProviderContainer extends React.PureComponent<WorkbookSpaceProviderContainerProps, WorkbookSpaceProviderContainerState> {

  private unmounted: boolean = false;

  public constructor(props: WorkbookSpaceProviderContainerProps){
    super(props);
    this.state = InitialState;
  }

  public render() {
    if (this.state.renderChildren) {
      return this.props.renderWorkbookSpace({
        workbookDetail: {
          usageInfo: this.state.workbookInfo.usageInfo,
          usageInfoMaxValues: this.state.workbookInfo.usageInfoMaxValues,
          ...this.state.workbookDetailInfo
        },
        workbookActions: this.state.workbookActions,
        openDetail: this.wrappedOpenContentDetailDialog,
        addToFavorites: this.favorite,
        removeFromFavorites: this.unfavorite,
        sanitizer: this.props.sanitizer
      });
    } else if (this.state.hasError) {
      if (this.props.isRequestAccessEnabled) {
        return <RequestAccessError url={this.props.urls.exploreUrl()} contentIdentifier={this.props.workbookId} contentType={'workbook'} errorCode={this.state.errorCode}/>;
      }
      return <RouterError errorType={'workbooks'} urls={this.props.urls} />;
    } else {
      return null;
    }
  }

  public componentWillUnmount() {
    this.unmounted = true;
  }

  public componentDidMount() {
    this.getData();
  }

  public componentDidUpdate(prevProps: WorkbookSpaceProviderContainerProps) {
    if (this.changedWorkbook(prevProps)) {
      this.updateState(InitialState /* want the initial state for the new workbook */);
      this.getData();
      return;
    }

    // if we dont have all we need - do nothing
    if (!this.haveAllDataNeededToRender()) {
      return;
    }

    if (this.state.initialRender) {
      this.changeSubspace(false);

      this.setState({initialRender: false, renderChildren: true}, () => {
        if (this.props.publishResultDisplay) {
          PublishWorkbookAction.handlePublishedWorkbook(this.state.workbookDetailInfo, this.props.publishResultUrl);
          this.removePublishDialogUrlParams();
        }
      });

      this.props.workbooks.markRecentlyViewed(this.state.workbookDetailInfo.id);
      BrowserTitleService.setTitleWithPlaceEntityName({ placeEntityName: this.state.workbookDetailInfo.name });

    } else if (prevProps.subspaceName !== this.props.subspaceName) {
      this.changeSubspace(true);
    }
  }

  private getData = () => {
    this.getWorkbook();
    this.getWorkbookDetail();
    this.getActions();
  }

  private changedWorkbook(prevProps: WorkbookSpaceProviderContainerProps) {
    return !_.isUndefined(this.props.siteName) &&
      !_.isUndefined(this.props.workbookId) &&
      (prevProps.siteName !== this.props.siteName || prevProps.workbookId !== this.props.workbookId);
  }

  private haveAllDataNeededToRender() {
    return !_.isUndefined(this.state.workbookInfo) &&
      !_.isUndefined(this.state.workbookDetailInfo) &&
      !_.isUndefined(this.state.workbookActions) &&
      !_.isUndefined(this.state.location);
  }

  private getWorkbookDetail = async () => {
    try {
      const projectContainer = 'PROJECT';
      const workbookDetail = await this.props.workbooks.getById(this.props.workbookId);
      this.updateState({workbookDetailInfo: workbookDetail});
      const requestParam: GetLocationForContentRequestParams = {
        contentType: workbookDetail.contentType,
        contentLuid: workbookDetail.luid
      };
      let projectHierarchy: INamedItemInfo[] = [];
      let projectHierarchyWithType = null;
      if(workbookDetail.project) {
        projectHierarchy = await Projects.getProjectHierarchy(workbookDetail.project.id);
        projectHierarchyWithType = addContentTypeToProjectHierarchy(projectHierarchy);
      }
      const contentPath = (projectHierarchyWithType == null)? [workbookDetail] : [ ...projectHierarchyWithType, workbookDetail ];
      let contentLocation = undefined;
      if (FeatureFlags.personalSpaceEnabled) {
        try {
          await callViaApiService(requestParam).then(
            async (result: GetLocationForContentResponse) => {
              if (!this.unmounted){
                contentLocation = await result.contentLocation.type;
                this.populateBreadCrumb(contentPath, contentLocation);
              }
            }
          );
        } catch (e) {
          this.populateBreadCrumb(contentPath, projectContainer);
        }
      } else {
        this.populateBreadCrumb(contentPath, projectContainer);
      }
      this.props.fetchedAllData();
      // if the extract create feature disabled, we won't tigger the poll service.
      if (this.props.isWebExtractionEnabled) {
        this.pollAndUpdateExtractPendingStatus();
      }
    } catch (e) {
      this.updateState({hasError: true, errorCode: e as number});
    }
  }

  private populateBreadCrumb(contentPath: ISpaceTypeInfo[], contentLocation: string) {
    this.updateState({location: contentLocation});
    this.props.enterContentSpace(
      {
        item: this.state.workbookDetailInfo,
        spaceType: 'workbook',
        subspaceName: getEffectiveWorkbookSubspaceName(this.props.subspaceName, queryString.parse(this.props.routeLocationSearch)),
        contentLocation: contentLocation,
        ownerId: this.state.workbookDetailInfo.owner.id
      }
    );
    if (contentLocation === 'PERSONAL_SPACE') {
      populateBreadcrumbsForPS(contentPath, this.props.urls);
    } else {
      populateContentBreadcrumbs(contentPath, this.props.urls);
    }
  }

  private getWorkbook = async () => {
    try {
      const workbook = await this.props.workbooks.all()
        .withFilters([Filter.fieldEq('id', this.props.workbookId)])
        .getFirst();
      this.updateState({workbookInfo: workbook});
    } catch (e) {
      this.updateState({hasError: true, errorCode: e as number});
    }
  }

  private getActions = async () => {
    try {
      const actions = await this.props.workbooks.actions(this.props.workbookId);
      this.updateState({workbookActions: actions});
    } catch (e) {}
  }

  private pollAndUpdateExtractPendingStatus = async () => {
    try {
      this.props.extractActionStatusService.pollAndUpdateExtractActionStatus(this.state.workbookInfo);
      // if polling failed will do nothing
    } catch (e) {}
  }


  private updateState<K extends keyof WorkbookSpaceProviderContainerState>(newState: Pick<WorkbookSpaceProviderContainerState, K>): void {
    if (!this.unmounted) {
      this.setState(newState);
    }
  }

  private changeSubspace = (shouldNotFetchCounts: boolean) => {
    this.props.enterContentSpace(
      {
        item: this.state.workbookDetailInfo,
        spaceType: 'workbook',
        subspaceName: getEffectiveWorkbookSubspaceName(this.props.subspaceName, queryString.parse(this.props.routeLocationSearch)),
        contentLocation: this.state.location,
        ownerId: this.state.workbookDetailInfo.owner.id
      }
    );

    // If we just switched tabs we know content counts are up to date.
    // They will get set to false by the above EnterSpaceAction.
    if(shouldNotFetchCounts) {
      this.props.contentCountsUpToDate();
    }

    this.clearAndSetSortOrderFromURL();

    this.props.requestSpaceContents();
  }

  // This is ported version of clearAndSetSortOrderFromURL from SiteSpaceConfigHelper
  private clearAndSetSortOrderFromURL() {
    const contentTypes = [workbookSubspaceNameToExplorableItemType(this.props.subspaceName) as ExplorableItemType];
    const spaceInfo = {item: undefined, spaceType: 'workbook', subspaceName: this.props.subspaceName} as SpaceInfo;
    const defaultOrder = getDefaultOrderForContentTypesOrSpaceInfo(contentTypes, spaceInfo);
    const values = queryString.parse(this.props.routeLocationSearch);

    const orderToSet = getOrderClausesFromQueryParam(values[UrlParamKeys.Order], defaultOrder);
    this.props.setOrderAction(orderToSet);
  }

  private wrappedOpenContentDetailDialog = () => {
    const { workbookActions, workbookDetailInfo, workbookInfo, location} = this.state;

    const workbookWithUsageInfo = {
      usageInfo: workbookInfo.usageInfo,
      usageInfoMaxValues: workbookInfo.usageInfoMaxValues,
      ...workbookDetailInfo
    };
    this.props.openContentDetailDialog(workbookWithUsageInfo, workbookActions, location);
  }

  /**
   * Prevents the publish dialog from showing up multiple times because of state.hardReload
   * re-rendering this react component and the previousUrl being the same in the app state.
   */
  private removePublishDialogUrlParams = () => {
    const cleanWorkbookUrl = window.location.href;
    this.props.setPreviousUrl(cleanWorkbookUrl);
  }

  private favorite = () => {
    this.props.addContentFavorite(this.state.workbookDetailInfo);
  }

  private unfavorite = () => {
   this.props.removeContentFavorite(this.state.workbookDetailInfo);
  }
}

const WorkbookSpaceProviderServices: React.ComponentType<WorkbookSpaceNonServicesProps> = function WorkbookSpaceProviderServices(props: WorkbookSpaceNonServicesProps) {
  return (
    <WorkbookSpaceProviderContainer
      {...props}
      workbooks={Workbooks}
      sanitizer={sanitize}
      extractActionStatusService={ExtractActionStatusService}
    />
  );
};

const mapStateToProps: MapStateToProps<WorkbookSpaceProviderStateProps> = (state: AppState) => ({
  isRequestAccessEnabled: SessionSiteSelectors.isRequestAccessEnabled(state),
  isWebExtractionEnabled: CompositeSessionInfoSelectors.isWebExtractionEnabled(state)
});

const mapDispatchToProps: MapDispatchToProps<WorkbookSpaceProviderDispatchProps> = (dispatch) => bindActionCreators({
  contentCountsUpToDate,
  requestSpaceContents,
  enterContentSpace,
  setOrderAction,
  addContentFavorite,
  removeContentFavorite,
  setPreviousUrl,
  openContentDetailDialog
}, dispatch);

export const ConnectedWorkbookSpaceProviderContainer: React.ComponentClass<WorkbookSpaceProviderOwnProps>
  = connect(mapStateToProps, mapDispatchToProps)(WorkbookSpaceProviderServices);

const renderWorkbookSpace = (props: WorkbookSpaceOwnProps): JSX.Element => <ConnectedWorkbookSpace {...props} />;

export interface LocationPathProp {
  readonly routeLocationPath: string;
}
export type WorkbookSpaceProviderProps = LocationPathProp & Omit<WorkbookSpaceProviderOwnProps, 'renderWorkbookSpace' | 'fetchedAllData'>;

export interface WorkbookSpaceProviderState {
  readonly fetchedAllData: boolean;
}

export class WorkbookSpaceProvider extends React.PureComponent<WorkbookSpaceProviderProps, WorkbookSpaceProviderState> {
  public constructor(props: WorkbookSpaceProviderProps) {
    super(props);
    this.state = {fetchedAllData: false};
  }

  public render() {
    const queryUpdaterProps: QueryParamUpdateProviderOwnProps = {
      routeLocationPath: this.props.routeLocationPath,
      routeLocationSearch: this.props.routeLocationSearch,
      shouldUpdateContentTypes: true,
      shouldUpdateFilters: false,
      shouldUpdateSort: true,
      shouldUpdateBreadcrumbs: this.state.fetchedAllData
    };

    return (
      <ConnectedQueryParamUpdateProvider {...queryUpdaterProps}>
        <ConnectedWorkbookSpaceProviderContainer {...{...this.props, renderWorkbookSpace, fetchedAllData: this.fetchedAllData}} />
      </ConnectedQueryParamUpdateProvider>
    );
  }

  private fetchedAllData = () => {
    this.setState({fetchedAllData: true});
  }
}

function getEffectiveWorkbookSubspaceName(
  subspaceName: WorkbookSubspaceName,
  queryParams: { [i: string]: string },
): WorkbookSubspaceName {
  // Check if we're in dataconnection subspace.
  // This means we are in the datasources path and the content type filter is equal to dataconnection
  if (subspaceName === 'datasources') {
    const contentTypesParam = queryParams[ContentTypeFilterKey];
    const contentTypes = getContentTypesFromQueryParam(contentTypesParam, []);
    if (contentTypes.length === 1 && contentTypes[0] === 'dataconnection') {
      return 'dataconnections';
    }
  }

  return subspaceName;
}
